import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { TextEditingTyping } from '@sharefiledev/icons';
import { Button, Flex, Input, Space, Spin, theme, Typography } from 'antd';
import {
	ComponentTypes,
	createDrawerBaseClassName,
	Field,
	getDrawerBodyElementSelector,
} from '../../../../common/componentUtils';
import { useFetchDatasources } from '../../../../common/hooks/useFetchDatasources';
import { VariableFieldEmptyState } from '../../../../components/VariableFieldEmptyState';
import { Variable } from '../../../../data/interfaces/DocumentTemplate';
import {
	SchemaProperty,
	SchemaResponse,
} from '../../../../data/interfaces/SchemaResponse';
import { fetchSchema } from '../../../../data/variablesDataApi';
import { t } from '../../../../utils';
import { AddVariables } from '../../../Start/AddVariables';
import { DocumentMetaDataItem } from '../../../Start/DocumentMetaData/DocumentMetaData';
import { DropdownSection } from '../DropdownSection';
import { FieldsSection } from '../FieldsSection';
import { LeftPanelModal } from '../LeftPanelModal';

const { Text, Title, Paragraph } = Typography;

interface Props {
	variables: Variable[] | undefined;
	selectedVariable: Variable | null;
	onSelectVariable: (value: Variable) => void;
	onSelectField: (type: ComponentTypes | null) => void;
	selectedProperty: SchemaProperty;
	setSelectedProperty: (property: SchemaProperty) => void;
	onSaveVariables: () => void;
}

export const SystemFields = ({
	variables,
	selectedVariable,
	onSelectVariable,
	onSelectField,
	selectedProperty,
	setSelectedProperty,
	onSaveVariables,
}: Props) => {
	const [properties, setProperties] = useState<(Field & SchemaProperty)[]>([]);
	const [searchProperties, setSearchProperties] = useState<(Field & SchemaProperty)[]>(
		[]
	);
	const [searchedProperty, setSearchedProperty] = useState<string>(null);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const showModal = () => {
		setIsModalOpen(true);
	};

	const hideModal = () => {
		setIsModalOpen(false);
	};
	const { token } = theme.useToken();
	const getDropdownOptions = () => {
		const options: any[] = [];
		variables?.forEach(variable => {
			options.push({
				label: variable.name,
				value: variable.id,
			});
		});
		return options;
	};

	const handleSaveVariable = (variable: Variable) => {
		onSelectVariable(variable);
		onSaveVariables();
		fetchSchemaByVariable(variable);
		hideModal();
	};

	const fetchSchemaByVariable = async (variable: Variable) => {
		if (!variable) {
			return;
		}
		setSearchProperties([]);
		setProperties([]);
		setSelectedProperty(null);

		const response: SchemaResponse = await fetchSchema(
			variable.connectionRID,
			variable.entityId
		);

		if (response) {
			const responseProperties = response.properties;

			const mappedProperties = responseProperties.map(p => {
				const type = ComponentTypes[p.type] || ComponentTypes.TextComponent;

				const obj = {
					color: '#000',
					icon: TextEditingTyping,
					label: p.name,
					type,
				};

				return { ...p, ...obj };
			});

			setProperties(mappedProperties);
		} else {
			setProperties([
				{
					color: '#000',
					icon: null,
					label: 'No records found',
					type: '',
					draggable: false,
				} as any,
			]);
		}
	};

	async function onChange(e: string) {
		const variable = variables.find(v => v.id === e);
		onSelectVariable(variable);
		fetchSchemaByVariable(variable);
	}

	function searchPropertyOnChange(e: ChangeEvent<HTMLInputElement>) {
		const { value } = e.target;
		setSearchedProperty(value);

		const result = properties.filter(p => p.name.match(new RegExp(value, 'ig')));

		setSearchProperties(result);
	}

	useEffect(() => {
		fetchSchemaByVariable(variables?.[0]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isSearch = searchedProperty && searchedProperty.trim();

	const fields = isSearch ? searchProperties : properties;

	const [height, setHeight] = useState('100%');
	const fieldsContainerRef = useRef<HTMLDivElement>(null);
	const fieldsContainerHeightRef = useRef<number>(0); // Save fieldsContainerRef height when fields are available. This is used when window is re-sized with other tab being active otherwise fieldsContainerRef.clientHeight is 0 when System fields tab is not active
	const leftPanelSignerFieldsContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const fieldsContainerElement = fieldsContainerRef.current;
		if (fieldsContainerElement) {
			fieldsContainerHeightRef.current = fieldsContainerElement.clientHeight;
		}
	}, [fields?.length]);

	useEffect(() => {
		const leftPanelSignerFieldsContainerElement =
			leftPanelSignerFieldsContainerRef.current;
		const PADDING = 32;
		const drawerBodyElement = document.querySelector(
			getDrawerBodyElementSelector(createDrawerBaseClassName)
		);
		drawerBodyElement.scrollTop = 0; // reset the scroll position
		const thresholdHeight = 570;

		const resizeObserver = new ResizeObserver((entry: ResizeObserverEntry[]) => {
			const drawerBodyElementHeight = entry[0].contentRect.height;

			if (fields.length === 0) {
				leftPanelSignerFieldsContainerElement.style.height =
					drawerBodyElementHeight - PADDING + 'px';
				leftPanelSignerFieldsContainerElement.style.overflow = 'auto';
			} else {
				const refHeight = fieldsContainerHeightRef.current;
				const newHeight = Math.abs(drawerBodyElementHeight - refHeight) - PADDING;

				if (drawerBodyElementHeight <= thresholdHeight) {
					setHeight(thresholdHeight - refHeight + 'px');
					leftPanelSignerFieldsContainerElement.style.height =
						drawerBodyElementHeight - PADDING + 'px';
					leftPanelSignerFieldsContainerElement.style.overflow = 'auto';
				} else {
					setHeight(newHeight - PADDING / 2 + 'px');
					leftPanelSignerFieldsContainerElement.style.height = 'auto';
					leftPanelSignerFieldsContainerElement.style.overflow = 'hidden';
				}
			}
		});
		if (leftPanelSignerFieldsContainerRef) {
			resizeObserver.observe(drawerBodyElement);
		}
		return () => {
			if (leftPanelSignerFieldsContainerRef) {
				resizeObserver.unobserve(drawerBodyElement);
			}
		};
	}, [fields?.length]);

	const onFieldClick = (type: ComponentTypes, value: SchemaProperty) => {
		setSelectedProperty(value);
		onSelectField(type);
	};

	const { isDatasourcesFetched, dataSources } = useFetchDatasources();
	const hasNoThirdPartyIntegrationEnabled =
		isDatasourcesFetched && dataSources.length === 0;

	return (
		<Flex vertical ref={leftPanelSignerFieldsContainerRef}>
			{variables?.length === 0 ? (
				<Flex vertical gap={token.sizeXS}>
					<Title style={{ margin: 0 }} level={5}>
						{t('leftNav.systemFilled.title')}
					</Title>
					<Flex vertical style={{ gap: token.margin }}>
						<Paragraph style={{ fontSize: token.fontSizeSM, whiteSpace: 'pre-line' }}>
							{t('leftNav.systemFilled.descriptionNoVariables')}
						</Paragraph>

						{hasNoThirdPartyIntegrationEnabled && <VariableFieldEmptyState />}

						<AddManageActionButton
							onClick={showModal}
							label={t('leftNav.systemFilled.addVariables')}
							disabled={hasNoThirdPartyIntegrationEnabled}
						/>
					</Flex>
				</Flex>
			) : (
				<Flex vertical gap={token.size}>
					<Flex
						vertical
						gap={token.size}
						ref={fieldsContainerRef}
						style={{
							width: '100%',
						}}
					>
						<Space direction="vertical">
							<Title style={{ margin: 0 }} level={5}>
								{t('leftNav.systemFilled.title')}
							</Title>
							<Text style={{ fontSize: token.fontSizeSM }}>
								{t('leftNav.systemFilled.descriptionWithVariables')}
							</Text>
						</Space>
						<DropdownSection
							title={t('leftNav.systemFilled.variable')}
							description={t('leftNav.systemFilled.dropdownDescription')}
							onChange={onChange}
							options={getDropdownOptions()}
							value={selectedVariable?.id}
						/>
						<AddManageActionButton
							onClick={showModal}
							label={t('leftNav.systemFilled.manageVariables')}
						/>
						<Input
							value={searchedProperty}
							placeholder={t('leftNav.systemFilled.searchData')}
							onChange={searchPropertyOnChange}
							suffix={<SearchOutlined />}
						/>
					</Flex>
					{fields.length === 0 && (
						<>
							<Flex justify="center" align="center" style={{ height: '200px' }}>
								{!isSearch && <Spin />}
							</Flex>
							<Flex>
								{isSearch && (
									<FieldsSection>
										<FieldsSection.Fields
											fields={[
												{
													type: ComponentTypes.TextComponent,
													label: 'No records found',
													icon: null,
													color: '#000',
													draggable: false,
												},
											]}
											selectedField={selectedProperty}
											onClick={onFieldClick}
										/>
									</FieldsSection>
								)}
							</Flex>
						</>
					)}

					<Space
						direction="vertical"
						style={{
							overflow: 'auto',
							width: '100%',
							height,
						}}
					>
						<FieldsSection>
							<FieldsSection.Fields
								fields={fields}
								selectedField={selectedProperty}
								onClick={onFieldClick}
							/>
						</FieldsSection>
					</Space>
				</Flex>
			)}
			<LeftPanelModal
				title={t('variables.label')}
				isOpen={isModalOpen}
				onHideModal={hideModal}
			>
				<DocumentMetaDataItem description={t('variables.description')}>
					<AddVariables isShownInModal onSave={handleSaveVariable} />
				</DocumentMetaDataItem>
			</LeftPanelModal>
		</Flex>
	);
};

type AddManageActionButton = {
	onClick: () => void;
	label: string;
	disabled?: boolean;
};

const AddManageActionButton = ({
	onClick,
	label,
	disabled = false,
}: AddManageActionButton) => {
	return (
		<Button
			type="primary"
			size="small"
			ghost
			style={{ width: '100%' }}
			onClick={onClick}
			disabled={disabled}
		>
			{label}
		</Button>
	);
};
